import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Modal, Textarea } from '@ministry/ui/internal';

import { deactivateUser, type DeactivateUserResult } from '../../lib/admin-users.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { useT } from '../../i18n/strings.js';

export interface DeactivateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  onSuccess: (result: DeactivateUserResult) => void;
}

/**
 * SCREEN_SPECS.md I19: "Deactivation ... requiring named confirmation ... a failed
 * deactivation explicitly states the account is STILL ACTIVE — the opposite failure
 * (thinking someone was removed when they weren't) is the dangerous one here."
 */
export function DeactivateUserModal({ open, onOpenChange, userId, userName, onSuccess }: DeactivateUserModalProps): React.JSX.Element {
  const t = useT();
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => deactivateUser(userId, reason.trim()),
    onSuccess: (result) => {
      onOpenChange(false);
      onSuccess(result);
    },
  });

  const reasonError = touched && reason.trim().length < 3 ? t('admin.deactivate.reason.error') : undefined;

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) {
      return (
        <AlertBanner variant="critical" title={t('admin.error.network')}>
          <p>{t('admin.deactivate.error.stillActive')}</p>
        </AlertBanner>
      );
    }
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === 'CONFLICT_STATE') {
        return <AlertBanner variant="warning" title={t('admin.deactivate.error.selfOrConflict')} />;
      }
      if (mutation.error.code === 'NOT_FOUND') {
        return <AlertBanner variant="critical" title={t('admin.deactivate.error.notFound')} />;
      }
      return (
        <AlertBanner variant="critical" title={t('admin.error.generic')}>
          <p>{t('admin.deactivate.error.stillActive')}</p>
        </AlertBanner>
      );
    }
    return null;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.deactivate.title', { name: userName })}
      description={t('admin.deactivate.description', { name: userName })}
      confirmLabel={mutation.isPending ? t('admin.deactivate.submitting') : t('admin.deactivate.submit')}
      cancelLabel={t('admin.common.cancel')}
      variant="destructive"
      loading={mutation.isPending}
      confirmDisabled={reason.trim().length < 3}
      onConfirm={() => {
        setTouched(true);
        if (reason.trim().length < 3) return;
        mutation.mutate();
      }}
    >
      <div className="flex flex-col gap-4">
        {banner}
        <Textarea
          id="admin-deactivate-reason"
          label={t('admin.deactivate.reason.label')}
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          onBlur={() => setTouched(true)}
          errorText={reasonError}
        />
      </div>
    </Modal>
  );
}
